import React from 'react';

export interface StatCard {
  label: string;
  value: string | number;
  icon?: React.ReactNode;
  color?: 'blue' | 'green' | 'red' | 'yellow' | 'purple' | 'gray';
  subtitle?: string;
}

const colorMap: Record<string, string> = {
  blue: 'text-blue-600 bg-blue-100',
  green: 'text-green-600 bg-green-100',
  red: 'text-red-600 bg-red-100',
  yellow: 'text-yellow-600 bg-yellow-100',
  purple: 'text-purple-600 bg-purple-100',
  gray: 'text-gray-600 bg-gray-100',
};

const accentMap: Record<string, string> = {
  blue: 'border-t-blue-500',
  green: 'border-t-green-500',
  red: 'border-t-red-500',
  yellow: 'border-t-yellow-500',
  purple: 'border-t-purple-500',
  gray: 'border-t-gray-400',
};

interface StatsCardsProps {
  stats: StatCard[];
  columns?: 2 | 3 | 4 | 5;
}

const gridColsMap: Record<number, string> = {
  1: 'md:grid-cols-1',
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
  4: 'md:grid-cols-4',
  5: 'md:grid-cols-5',
};

const StatsCards: React.FC<StatsCardsProps> = ({ stats, columns = 4 }) => {
  if (stats.length === 0) return null;
  // Never render more columns than cards — a 4-column grid with 2 cards
  // leaves ugly empty space
  const count = Math.max(1, Math.min(columns, stats.length));
  const gridCols = gridColsMap[count];

  return (
    <div className={`grid grid-cols-1 ${count > 1 ? 'sm:grid-cols-2' : ''} ${gridCols} gap-6`}>
      {stats.map((stat, i) => {
        const colors = colorMap[stat.color || 'blue'];
        const accent = accentMap[stat.color || 'blue'];
        return (
          <div key={i} className={`card p-6 border-t-4 ${accent}`}>
            <div className="flex items-center">
              {stat.icon && (
                <div className={`p-3 rounded-full ${colors.split(' ')[1]}`}>
                  <span className={colors.split(' ')[0]}>{stat.icon}</span>
                </div>
              )}
              <div className={`min-w-0 ${stat.icon ? 'ml-4' : ''}`}>
                <p className="text-sm font-medium text-gray-600 truncate" title={stat.label}>{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900 break-words">{stat.value}</p>
                {stat.subtitle && <p className="text-sm text-gray-500">{stat.subtitle}</p>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default StatsCards;
